/**
 * Session persistence for the public gift-card OTP challenge.
 *
 * Security invariants (Stage 4/6):
 *  - ONLY non-sensitive challenge routing data is persisted: the opaque
 *    challenge id, its expiry and the resend-cooldown deadline.
 *  - The OTP code, the reveal token, the reveal id and the full gift-card code
 *    are NEVER persisted — they stay in React state only.
 *  - The gift view token itself is never stored; only a non-reversible-enough
 *    fingerprint used to bind the stored challenge to the same gift link, so a
 *    challenge from another card/link can never be resumed.
 *  - `sessionStorage` only (per-tab, cleared when the tab closes).
 */

export const GIFT_OTP_SESSION_KEY = 'riham.gift.otp.challenge.v1';

export interface StoredChallenge {
  challengeId: string;
  /** Epoch ms when the challenge stops being usable. */
  expiresAt: number;
  /** Epoch ms until which "resend" stays disabled. */
  cooldownUntil: number;
}

interface Persisted extends StoredChallenge {
  fp: string;
}

/** Stable, non-secret fingerprint of the gift view token (binding only). */
export function fingerprintToken(token: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < token.length; i++) {
    const c = token.charCodeAt(i);
    h1 = (h1 ^ c) * 16777619 >>> 0;
    h2 = (h2 + c * (i + 1)) >>> 0;
  }
  return `${h1.toString(36)}.${h2.toString(36)}.${token.length}`;
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function clearChallenge(): void {
  try {
    storage()?.removeItem(GIFT_OTP_SESSION_KEY);
  } catch {
    /* storage unavailable — nothing to clean */
  }
}

export function saveChallenge(token: string, challenge: StoredChallenge): void {
  const store = storage();
  if (!store) return;
  const payload: Persisted = { ...challenge, fp: fingerprintToken(token) };
  try {
    store.setItem(GIFT_OTP_SESSION_KEY, JSON.stringify(payload));
  } catch {
    /* quota/private mode — persistence is best-effort */
  }
}

/**
 * Returns the pending challenge for this exact gift token, or null.
 * Expired entries and entries belonging to another token are removed.
 */
export function loadChallenge(token: string, now: number = Date.now()): StoredChallenge | null {
  const store = storage();
  if (!store) return null;
  let raw: string | null = null;
  try {
    raw = store.getItem(GIFT_OTP_SESSION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: Partial<Persisted> | null = null;
  try {
    parsed = JSON.parse(raw) as Partial<Persisted>;
  } catch {
    clearChallenge();
    return null;
  }

  if (!parsed || typeof parsed.challengeId !== 'string' || !parsed.challengeId) {
    clearChallenge();
    return null;
  }
  if (parsed.fp !== fingerprintToken(token)) {
    clearChallenge();
    return null;
  }
  const expiresAt = Number(parsed.expiresAt ?? 0);
  if (!Number.isFinite(expiresAt) || expiresAt <= now) {
    clearChallenge();
    return null;
  }
  const cooldownUntil = Number(parsed.cooldownUntil ?? 0);
  return {
    challengeId: parsed.challengeId,
    expiresAt,
    cooldownUntil: Number.isFinite(cooldownUntil) ? cooldownUntil : 0,
  };
}

/** Remaining whole seconds until `deadline` (never negative). */
export function secondsUntil(deadline: number, now: number = Date.now()): number {
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}
